<template>
  <q-btn-dropdown
    align="left"
    class="dropdown-btn full-width"
    :class="{
      'full-height': fullHeight,
      borderless: borderless,
    }"
    content-class="box-shadow"
    menu-anchor="bottom middle"
    menu-self="top middle"
    :dense="dense"
    :disable="disabled"
    @show="onDropdownShow"
  >
    <template #label>
      <div v-if="innerLabel && btnLabel" class="row items-center no-wrap full-width relative-position">
        <div class="inner-label">{{ innerLabel }}</div>
        <div
          class="text-left text-active dropdown-text"
          :class="{
            'clearable-icon': clearable,
            'text-negative': errorHighlight,
          }"
        >
          <br />{{ btnLabel }}
        </div>
      </div>
      <div
        v-else
        class="text-left dropdown-text"
        :class="{
          'clearable-icon': clearable,
          'text-negative': errorHighlight,
        }"
      >
        {{ btnLabel || label }}
      </div>
      <q-icon v-if="clearable && btnLabel" name="clear" class="clear-dropdown-icon" @click="clearSelection($event)" />
    </template>
    <q-list :data-testid="identifier" bordered dense>
      <q-item v-show="showSearch">
        <q-item-section>
          <q-input
            ref="refFilterText"
            v-model="filterText"
            color="input"
            :label="t('search')"
            borderless
            @keyup.down="onSearchDownKeyPress"
            @keyup.up="onSearchUpKeyPress"
          >
            <template #prepend>
              <q-icon name="search" />
            </template>
            <template #append>
              <q-icon name="close" class="cursor-pointer" @click="filterText = ''" />
            </template>
          </q-input>
        </q-item-section>
      </q-item>
      <q-separator />
      <q-item
        v-for="item of filteredItems"
        :key="item.name"
        ref="itemsRef"
        v-close-popup
        clickable
        :dropdown-item="item.name"
        @click="selectItem(item)"
        @keyup.down="onSearchDownKeyPress"
        @keyup.up="onSearchUpKeyPress"
      >
        <q-item-section>
          <q-item-label>{{ item.name }}</q-item-label>
        </q-item-section>
      </q-item>
      <q-separator />
    </q-list>
  </q-btn-dropdown>
</template>

<script lang="ts" setup>
import { ref, toRefs, watch, onMounted, computed, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { DropdownItem } from '@/components/_common/dropdown/DropdownItem';
import { normalizeString } from '@/utils';
import { QItem } from 'quasar';

/*****************************************************************************
 * PROPS
 ****************************************************************************/
type Props = {
  identifier?: string;
  items?: DropdownItem[];
  label?: string;
  innerLabel?: string;
  selectedItemValue?: string | number | null;
  nullItem?: boolean;
  nullItemLabel?: string | null;
  clearable?: boolean;
  errorHighlight?: boolean;
  showSearch?: boolean;
  fullHeight?: boolean;
  dense?: boolean;
  borderless?: boolean;
  disabled?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  identifier: '',
  items: () => [],
  label: '',
  innerLabel: '',
  selectedItemValue: null,
  nullItem: false,
  nullItemLabel: null,
  clearable: false,
  errorHighlight: false,
  showSearch: true,
  fullHeight: false,
  dense: false,
  borderless: false,
  disabled: false,
});

/*****************************************************************************
 * EMITS
 ****************************************************************************/
const emit = defineEmits(['update:selectedItemValue']);

/*****************************************************************************
 * SETUP
 ****************************************************************************/
const { selectedItemValue, items, nullItem } = toRefs(props);
const { t } = useI18n();
const nullDropDownItem: DropdownItem = {
  name: props.nullItemLabel ?? t('none'),
  value: null,
};

const btnLabel = ref('');
const localItems = ref<DropdownItem[]>([]);

const clearSelection = (event: Event) => {
  event.stopPropagation();
  btnLabel.value = '';
  emit('update:selectedItemValue', '');
};
const selectItem = (item) => {
  btnLabel.value = item.name;
  emit('update:selectedItemValue', item.value);
};
const setSelectedItem = (searchItemValue: string | number | null): void => {
  const selectedItem = localItems.value.find((item) => item.value === searchItemValue);
  if (selectedItem) {
    btnLabel.value = selectedItem.name;
  }
};

const addNullItem = () => {
  if (nullItem?.value && localItems.value[0]?.value !== null) {
    localItems.value = [nullDropDownItem, ...localItems.value];
  }
};

watch(selectedItemValue, () => {
  setSelectedItem(selectedItemValue.value);
});

watch(items, () => {
  localItems.value = items.value;
  setSelectedItem(selectedItemValue.value);
  addNullItem();
});

onMounted(() => {
  localItems.value = items.value;
  setSelectedItem(selectedItemValue.value);
  addNullItem();
});

const filterText = ref('');
const filteredItems = computed(() => {
  return localItems.value.filter((item) => {
    if (filterText.value === '') {
      return true;
    }
    return normalizeString(`${item.name.toLocaleLowerCase()}${item.search?.toLocaleLowerCase()}`).includes(
      normalizeString(filterText.value).toLocaleLowerCase(),
    );
  });
});

/*****************************************************************************
 * FOCUSING ELEMENTS
 ****************************************************************************/
const refFilterText = ref();
const itemsRef = ref<InstanceType<typeof QItem>[]>([]);
const activeFocusIndex = ref<null | number>(null);
const onDropdownShow = () => {
  activeFocusIndex.value = null;
  nextTick(() => refFilterText.value.focus());
};

const onSearchUpKeyPress = () => {
  // In case there's only 1 element (probably after filtering)
  // refocus this element on arrow hit
  if (activeFocusIndex.value === 0) {
    itemsRef.value[activeFocusIndex.value].$el.focus();
    return;
  }

  activeFocusIndex.value =
    activeFocusIndex.value === null ? filteredItems.value.length - 1 : activeFocusIndex.value - 1;
  itemsRef.value[activeFocusIndex.value].$el.focus();
};

const onSearchDownKeyPress = () => {
  // In case there's only 1 element (probably after filtering)
  // refocus this element on arrow hit
  if (activeFocusIndex.value === filteredItems.value.length - 1) {
    itemsRef.value[activeFocusIndex.value].$el.focus();
    return;
  }

  activeFocusIndex.value = activeFocusIndex.value === null ? 0 : activeFocusIndex.value + 1;
  itemsRef.value[activeFocusIndex.value].$el.focus();
};

watch(filteredItems, (val, prevVal) => {
  if (val.length !== prevVal.length) {
    activeFocusIndex.value = null;
  }
});
</script>

<style lang="scss" scoped>
@import 'styles/app.variables';
.dropdown-text {
  width: calc(100% - 18px);
  overflow: hidden;
  text-overflow: ellipsis;
  &.clearable-icon {
    width: calc(100% - 45px);
  }
}
body.body--dark {
  .q-icon {
    color: nth($text-color, 1) !important;
  }
}
body.body--light {
  .q-icon {
    color: nth($text-color, 1) !important;
  }
}
</style>
